import React, { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { formatIST, timeAgo } from '@/utils/format';

export default function ArtifactsView() {
  const { artifacts, setArtifacts } = useAppStore();
  const [tab, setTab] = useState('all'); // 'all', 'code', 'document', 'starred', 'history'
  const [selectedArtifact, setSelectedArtifact] = useState(null);

  const filtered = artifacts.filter(a => {
    if (tab === 'starred') return a.starred;
    if (tab === 'history') return a.deletedAt;
    if (tab === 'all') return !a.deletedAt;
    return a.type === tab && !a.deletedAt;
  });

  const typeColor = (type) => {
    switch (type) {
      case 'code': return { bg: 'bg-[#244c2a]', text: 'text-[#71e184]' };
      case 'document': return { bg: 'bg-[#323335]', text: 'text-[#d5d6d9]' };
      case 'component': return { bg: 'bg-[#4c2424]', text: 'text-[#e17171]' };
      default: return { bg: 'bg-[#323335]', text: 'text-[#d5d6d9]' };
    }
  };

  const toggleStar = (id) => {
    setArtifacts(artifacts.map(a => a.id === id ? { ...a, starred: !a.starred } : a));
  };

  const deleteArtifact = (id) => {
    setArtifacts(artifacts.map(a => a.id === id ? { ...a, deletedAt: Date.now() } : a));
  };

  return (
    <div className="flex-1 flex flex-col p-4 md:p-6 overflow-y-auto w-full bg-[#232426]">
      <h1 className="text-white font-bold text-[18px] md:text-xl mb-6">Artifacts</h1>

      {/* Tabs */}
      <div className="flex items-center gap-5 mb-6">
        <div className="flex bg-transparent border border-[#515153] rounded-[4px] overflow-hidden text-[13.5px] font-medium h-[30px]">
          {[
            { id: 'all', label: 'All' },
            { id: 'code', label: 'Code' },
            { id: 'document', label: 'Documents' },
            { id: 'starred', label: 'Starred' },
            { id: 'history', label: 'History' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 h-full transition-colors flex items-center justify-center border-r border-[#515153] last:border-r-0 ${tab === t.id ? 'bg-[#784A15] text-[#ED8B38]' : 'text-[#d5d6d9] hover:bg-[#323335]'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="text-[#8f9196] text-[13px]">{filtered.length} items</span>
      </div>

      {/* Content */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* List */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[#8f9196]">
              <svg className="w-12 h-12 mb-3 text-[#515153]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              <p className="text-[14px]">{tab === 'history' ? 'No deleted artifacts' : 'No artifacts yet'}</p>
              <p className="text-[12px] mt-1">Artifacts are created automatically when Palinos generates code or documents in chat.</p>
            </div>
          ) : (
            filtered.map(a => {
              const tc = typeColor(a.type);
              return (
                <div
                  key={a.id}
                  onClick={() => setSelectedArtifact(a)}
                  className={`bg-[#323335] border border-[#515153] rounded-md p-4 cursor-pointer hover:border-[#6498CC] transition-all group ${selectedArtifact?.id === a.id ? 'border-[#6498CC]' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`${tc.bg} ${tc.text} text-[10px] px-1.5 py-0.5 rounded font-bold uppercase`}>{a.type}</span>
                        {a.language && <span className="text-[#8f9196] text-[11px]">{a.language}</span>}
                        <span className="text-[#8f9196] text-[11px]">v{a.version}</span>
                      </div>
                      <div className="text-white text-[14px] font-medium truncate">{a.title}</div>
                      <div className="text-[#8f9196] text-[12px] mt-1 flex gap-3">
                        <span>Created {timeAgo(a.createdAt)}</span>
                        {a.updatedAt !== a.createdAt && <span>Edited {timeAgo(a.updatedAt)}</span>}
                        {a.deletedAt && <span className="text-[#e17171]">Deleted {timeAgo(a.deletedAt)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); toggleStar(a.id); }} className={`p-1 rounded transition-colors ${a.starred ? 'text-[#ED8B38]' : 'text-[#8f9196] hover:text-white'}`}>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill={a.starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                      </button>
                      {!a.deletedAt && (
                        <button onClick={(e) => { e.stopPropagation(); deleteArtifact(a.id); }} className="p-1 text-[#8f9196] hover:text-[#e17171] rounded transition-colors">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Preview Panel */}
        {selectedArtifact && (
          <div className="hidden lg:flex flex-col w-[400px] bg-[#1d1e22] border border-[#515153] rounded-md overflow-hidden">
            <div className="p-3 border-b border-[#515153] flex items-center justify-between bg-[#232426]">
              <div className="text-white text-[13px] font-medium truncate">{selectedArtifact.title}</div>
              <button onClick={() => setSelectedArtifact(null)} className="text-[#8f9196] hover:text-white">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-[12px] text-[#d5d6d9] font-mono leading-relaxed">
              {selectedArtifact.content}
            </pre>
            <div className="p-3 border-t border-[#515153] bg-[#232426] flex justify-between text-[11px] text-[#8f9196]">
              <span>{selectedArtifact.metadata?.lineCount || '—'} lines</span>
              <span>{selectedArtifact.metadata?.wordCount || '—'} words</span>
              <span>v{selectedArtifact.version}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
